import { motion } from 'framer-motion';
import { useTranslation } from 'react-i18next';
import { useClickWithSound } from '@/shared/hooks/useClickWithSound';
import { createResponsiveTextStroke } from '@/styles/textStroke';

interface WaitingPlayersBadgeProps {
  count: number;
  variant: 'free' | 'paid';
  onClick: () => void;
}

const variantStyles = {
  free: {
    gradient: 'linear-gradient(180deg, #5BB8F0 0%, #3B9FE8 40%, #1A7FD5 100%)',
    bottomEdge: '#0D5BA3',
    shadow: '0 4px 8px rgba(13, 91, 163, 0.5)',
    dotColor: 'bg-green-400',
    highlight: 'inset 0 1px 0 rgba(255,255,255,0.35)'
  },
  paid: {
    gradient: 'linear-gradient(180deg, #FFC234 0%, #F9A825 40%, #E88A0C 100%)',
    bottomEdge: '#B5690A',
    shadow: '0 4px 8px rgba(181, 105, 10, 0.5)',
    dotColor: 'bg-white',
    highlight: 'inset 0 1px 0 rgba(255,255,255,0.4)'
  }
} as const;

export function WaitingPlayersBadge({ count, variant, onClick }: WaitingPlayersBadgeProps) {
  const { t } = useTranslation();
  const handleClick = useClickWithSound(onClick, undefined);

  const styles = variantStyles[variant];
  const label =
    variant === 'free' ? t('waitingPlayers.free', { count }) : t('waitingPlayers.paid', { count });

  return (
    <motion.button
      type="button"
      initial={{ x: '120%' }}
      animate={{ x: 0 }}
      exit={{ x: '120%' }}
      transition={{ type: 'spring', damping: 20, stiffness: 300 }}
      whileTap={{ y: 2 }}
      onClick={handleClick}
      className="relative cursor-pointer"
    >
      {/* 3D bottom edge */}
      <div
        className="absolute inset-0 top-1 rounded-l-2xl"
        style={{ background: styles.bottomEdge }}
      />
      {/* Main button face */}
      <div
        className="relative flex items-center gap-2 rounded-l-2xl pl-3 pr-4 py-2.5 border border-r-0 border-white/15"
        style={{
          background: styles.gradient,
          boxShadow: `${styles.shadow}, ${styles.highlight}`
        }}
      >
        <span className="relative flex h-2.5 w-2.5 shrink-0">
          <span
            className={`animate-ping absolute inline-flex h-full w-full rounded-full ${styles.dotColor} opacity-75`}
          />
          <span className={`relative inline-flex rounded-full h-2.5 w-2.5 ${styles.dotColor}`} />
        </span>
        <span
          className="font-mind-boggle text-white text-sm leading-none whitespace-nowrap"
          style={createResponsiveTextStroke(0.5, 0.04)}
        >
          {label}
        </span>
      </div>
    </motion.button>
  );
}
